import { usePlacesWidget } from 'react-google-autocomplete'

export const getLocalizedLabels = (country: string) => {
  switch (country) {
    case 'US':
      return {
        state: 'State',
        zipCode: 'Zip Code',
      }
      break
    case 'CA':
      return {
        state: 'Province',
        zipCode: 'Postal Code',
      }
      break
    default:
      return {
        state: 'Region',
        zipCode: 'Postal Code',
      }
  }
}

type setter<T> = (x: T | undefined) => void

interface GoogleAutoCompleteInput {
  enableLine2: boolean
  setShowLine2Input: setter<boolean>
  setAddress1: setter<string>
  setAddress2: setter<string> | null
  setCity: setter<string>
  setState: setter<string>
  setCountry: setter<string>
  setZipcode: setter<string>
  triggerValidation: () => void
}
export const useGoogleAutoComplete = ({
  enableLine2,
  setShowLine2Input,
  setAddress1,
  setAddress2,
  setCity,
  setState,
  setCountry,
  setZipcode,
  triggerValidation,
}: GoogleAutoCompleteInput) =>
  usePlacesWidget<HTMLElement>({
    apiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
    onPlaceSelected: (place: google.maps.places.PlaceResult) => {
      const address = place?.address_components
      if (!address) return

      const extract = (label: string) =>
        address.find((component) => component.types.includes(label))

      const streetNumber = extract('street_number')?.long_name

      const route = extract('route')?.long_name

      let line1 = `${streetNumber ? streetNumber + ' ' : ''}${route}`

      const line2 = extract('subpremise')?.long_name

      if (enableLine2) {
        line2 ? setShowLine2Input(true) : setShowLine2Input(false)
      } else {
        line1 = `${line1}${line2 ? ', ' + line2 : ''}`
      }

      const country = extract('country')?.short_name

      /** Google maps api stores "city" information differently depending on location
       * In most cases, "locality" should be preferred
       * In the case of NYC, there is no "locality" information and only "sublocality_level_1"
       * In the UK, "posial_town" is used for city
       * Countries like Brazil use sublocality for neighborhoods, and
       * administrative_area_level_2 for cities
       * US uses administrative_area_level_2 for county information, so
       * we should never use that for the city
       * More info: https://developers-dot-devsite-v2-prod.appspot.com/maps/documentation/javascript/examples/places-autocomplete-addressform
       */
      const city =
        country === 'US'
          ? (
              extract('locality') || // ex: San Diego
              extract('sublocality_level_1') || // ex: Staten Island, Brooklyn
              // administrative_area_level_X's are usually empty, but just in case
              extract('administrative_area_level_3') ||
              extract('administrative_area_level_4') ||
              extract('administrative_area_level_5')
            )?.long_name
          : (
              extract('locality') ||
              extract('postal_town') || // UK uses postal_town for city
              extract('administrative_area_level_2') || // Brazil uses adminstrative_area_level_2 for city
              extract('administrative_area_level_3') ||
              extract('administrative_area_level_4') ||
              extract('administrative_area_level_5')
            )?.long_name

      const state = extract('administrative_area_level_1')?.short_name

      const postalCode = extract('postal_code')?.long_name

      const postalCodeSuffix = extract('postal_code_suffix')?.long_name

      const zipcode = postalCodeSuffix ? `${postalCode}-${postalCodeSuffix}` : postalCode

      setAddress1(line1)
      if (setAddress2 && line2) setAddress2(line2)
      setCity(city)
      setState(state)
      setCountry(country)
      setZipcode(zipcode)
      triggerValidation()
    },
    options: {
      types: ['address'],
    },
  })
